import React, { useEffect, useState } from 'react';
import { useNavigation } from '../context/NavigationContext';
import { getPendingRiders, updateRiderStatus } from '../api/riderApproval';

function errorMessage(err, fallback) {
  const detail = err.response?.data?.detail;
  if (!detail) return err.message || fallback;
  if (typeof detail === 'string') return detail;
  if (detail.message) return typeof detail.message === 'string' ? detail.message : JSON.stringify(detail.message);
  return JSON.stringify(detail);
}

export default function RiderApproval() {
  const { navigate } = useNavigation();
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const body = await getPendingRiders();
      setRiders(body.riders || []);
    } catch (err) {
      setError(errorMessage(err, 'Failed to load pending riders'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function act(rider, nextStatus) {
    setBusyId(rider.rider_id);
    setError(null);
    setSuccess(null);
    try {
      await updateRiderStatus(rider.rider_id, nextStatus);
      setSuccess(
        `${rider.full_name || 'Rider'} ${nextStatus === 'active' ? 'approved' : 'rejected'}.`
      );
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Status update failed'));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="p-6">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Rider Approval</h2>
          <p className="text-sm text-gray-400 mt-1">
            Riders awaiting approval before they can go on shift.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="text-sm font-semibold text-gray-500 hover:text-zoddle-green transition-colors"
          >
            Refresh
          </button>
          <button
            onClick={() => navigate('riderassign')}
            className="text-sm font-semibold border border-gray-200 text-gray-600 px-3 py-2 rounded hover:bg-gray-50"
          >
            Rider Operations
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-100 text-zoddle-pink text-sm px-4 py-3 rounded">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 bg-green-50 border border-green-100 text-green-700 text-sm px-4 py-3 rounded">
          {success}
        </div>
      )}

      {loading && <div className="text-gray-500">Loading...</div>}

      {!loading && riders.length === 0 && !error && (
        <div className="text-gray-400 text-sm">No riders awaiting approval.</div>
      )}

      {!loading && riders.length > 0 && (
        <div className="bg-white border border-gray-200 rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Phone</th>
                <th className="text-left px-4 py-2">Warehouse</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {riders.map(rider => (
                <tr key={rider.rider_id} className="border-t border-gray-100">
                  <td className="px-4 py-3 font-medium text-gray-800">{rider.full_name || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{rider.phone_e164 || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{rider.warehouse_code || '—'}</td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <button
                      onClick={() => act(rider, 'active')}
                      disabled={busyId === rider.rider_id}
                      className="text-sm font-semibold bg-zoddle-green text-white px-3 py-1.5 rounded hover:opacity-90 disabled:opacity-50"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => act(rider, 'suspended')}
                      disabled={busyId === rider.rider_id}
                      className="text-sm font-semibold border border-zoddle-pink text-zoddle-pink px-3 py-1.5 rounded hover:bg-red-50 disabled:opacity-50"
                    >
                      Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
